/**
 * DomesticPage.jsx
 * Premium Light-mode Domestic Sports Betting Page
 * Horizontal combination tabs, multi-row odds tables, and detailed guidelines widget
 * — Connected to /sportsnew/* API via SportsnewContext
 */
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import './DomesticPage.css';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsnewContext } from '../../../_context/SportsnewContext';
import { SPORTSNEW_LIST_STATUS } from '../../../_context/_helpers/sportsnewListRequestState';
import { formatSportsMatchTime } from '../_helpers/sportsMatchTime';
import { hasMainOddsDraw } from '../_helpers/mainOddsPolicy';
import { countryFlagEmoji, resolveSportsnewCountryName } from '../_helpers/countryFlags';
import { getSportsnewSportLabel } from '../_helpers/liveDisplay';
import { sportsnewSelectionIdentity } from '../_helpers/sportsnewSelectionIdentity';
import {
  domesticMarketRole,
  normalizeDomesticSport,
  oddsForDomesticMarket,
  resolveDomesticTwoWayOdds,
  selectDomesticAdditionalMarkets,
  selectDomesticDefaultMarkets,
} from '../_helpers/domesticMarketPolicy';
import getSelectionLabel from './selectionLabelResolver';

// Retain Crypto's existing three banners across responsive layouts.
import slide1 from '../../../assets/Slider/slide1.png';
import slide2 from '../../../assets/Slider/slide2.png';
import slide3 from '../../../assets/Slider/slide3.png';

// Team jersey images
import dressRedImage from '../../../assets/away_team_red.png';
import dressBlueImage from '../../../assets/away_team_blue.png';

// Sport icons
import soccerIcon from '../../../assets/Games/1.svg';
import basketballIcon from '../../../assets/Games/2.svg';
import baseballIcon from '../../../assets/Games/3.svg';
import hockeyIcon from '../../../assets/Games/4.svg';
import volleyballIcon from './assets/valleyball.png';

// Lucide icons
import { BarChart2, ReceiptText, Loader, Lock, Minus, Plus } from 'lucide-react';
import { DpMatchListSkeleton } from '../../../components/Skeleton/Skeleton';

const SLIDER_IMAGES = [
  { desktop: slide1, mobile: slide1 },
  { desktop: slide2, mobile: slide2 },
  { desktop: slide3, mobile: slide3 },
];

const SPORT_TABS = [
  { id: 'soccer',     icon: soccerIcon,     label: '축구조합' },
  { id: 'baseball',   icon: baseballIcon,   label: '야구조합' },
  { id: 'basketball', icon: basketballIcon, label: '농구조합' },
  { id: 'ice-hockey', icon: hockeyIcon,     label: '하키조합' },
  { id: 'volleyball', icon: volleyballIcon, label: '배구조합' },
];

const SPORT_ICON_BY_SLUG = {
  soccer: soccerIcon,
  baseball: baseballIcon,
  basketball: basketballIcon,
  'ice-hockey': hockeyIcon,
  volleyball: volleyballIcon,
};
const SPORT_LABEL_BY_SLUG = Object.fromEntries(SPORT_TABS.map((tab) => [tab.id, tab.label]));
const sportVisibleInScope = (category) => (
  category
  && category.active !== false
  && category.slug !== 'esports'
  && category.domesticEnabled !== false
);
const domesticSportTabsFromCategories = (categories) => {
  const rows = Array.isArray(categories) ? categories.filter(sportVisibleInScope) : [];
  if (rows.length === 0) return SPORT_TABS;
  return rows.map((category) => {
    const baseLabel = category.nameKo || category.name || SPORT_LABEL_BY_SLUG[category.slug] || category.slug;
    return {
      id: category.slug,
      icon: SPORT_ICON_BY_SLUG[category.slug] || soccerIcon,
      label: baseLabel.endsWith('조합') ? baseLabel : `${baseLabel}조합`,
    };
  });
};

/* ── Resolve home/away logo — falls back to jersey images ── */
const resolveTeamLogo = (url, side) =>
  url || (side === 'home' ? dressBlueImage : dressRedImage);

/* ── Safely format odds value ── */
const fmtOdds = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

/* ── Build the domestic cross-betting rows from a fixture ── */
const marketOddsFromMarket = oddsForDomesticMarket;

const oddIdOf = (odd) => odd?.oddStateId ?? odd?.id ?? odd?.odd_state_id ?? null;
const priceOfOdd = (odd) => odd?.priceDecimal ?? odd?.price_decimal ?? odd?.odds ?? odd?.price ?? null;
const bettableOfOdd = (odd) => odd?.bettable ?? odd?.isBettable ?? odd?.is_bettable ?? false;

const toCartOdd = (odd, market = null) => {
  const id = oddIdOf(odd);
  if (id == null) return null;
  return {
    ...odd,
    id,
    oddStateId: id,
    priceDecimal: priceOfOdd(odd),
    name: odd.name ?? odd.displayName ?? odd.selectionName ?? odd.selection,
    label: odd.label ?? odd.displayName ?? odd.name ?? odd.selectionName ?? odd.selection,
    bettable: bettableOfOdd(odd),
    providerOddId: odd.providerOddId ?? odd.provider_odd_id ?? null,
    oddIdentityKey: odd.oddIdentityKey ?? odd.odd_identity_key ?? null,
    ...sportsnewSelectionIdentity(odd),
    market_id: market?.marketStateId ?? market?.marketId ?? market?.id ?? market?.marketTypeKey ?? null,
    marketTypeKey: market?.marketTypeKey ?? market?.market_type_key ?? null,
    marketGroupId: market?.marketGroupId ?? null,
  };
};

const numericLine = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const signedLine = (value) => {
  const line = numericLine(value);
  if (line == null) return null;
  return `${line > 0 ? '+' : ''}${Number.isInteger(line) ? line : line.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
};

const lineOfOdd = (odd) => odd?.line ?? odd?.points ?? odd?.selectionLine ?? odd?.selection_line ?? odd?.canonicalLineKey ?? odd?.canonical_line_key ?? null;

const marketDisplayName = (market) => market?.displayNameKo
  ?? market?.display_name_ko
  ?? market?.displayName
  ?? market?.name
  ?? market?.marketName
  ?? market?.market_name
  ?? market?.marketTypeKey
  ?? market?.market_id
  ?? '마켓';

const buildHandicapRow = (market) => {
  if (!market) return null;
  const odds = marketOddsFromMarket(market);
  if (!odds.length) return null;
  const { left: homeRaw, right: awayRaw } = resolveDomesticTwoWayOdds(market, 'handicap');
  const homeLine = numericLine(lineOfOdd(homeRaw) ?? market.line);
  const awayLine = numericLine(lineOfOdd(awayRaw));
  return {
    type: 'handicap',
    centerLabel: 'HANDI',
    homeLine: signedLine(homeLine ?? (awayLine != null ? -awayLine : market.line)),
    awayLine: signedLine(awayLine ?? (homeLine != null ? -homeLine : null)),
    homeOdd: toCartOdd(homeRaw, market),
    drawOdd: null,
    awayOdd: toCartOdd(awayRaw, market),
  };
};

const buildTotalRow = (market) => {
  if (!market) return null;
  const odds = marketOddsFromMarket(market);
  if (!odds.length) return null;
  const { left: overRaw, right: underRaw } = resolveDomesticTwoWayOdds(market, 'total');
  const line = lineOfOdd(overRaw) ?? lineOfOdd(underRaw) ?? market.line ?? market.lineKey ?? market.canonicalLineKey;
  return {
    type: 'ou',
    centerLabel: 'O/U',
    line: line != null ? String(line).replace(/^\+/, '') : '—',
    homeOdd: toCartOdd(overRaw, market),
    drawOdd: null,
    awayOdd: toCartOdd(underRaw, market),
  };
};

const extractCrossRows = (fixture) => {
  const rows = [];
  const mo = fixture.mainOdds ?? {};

  const mkOdd = (src) => src?.oddStateId ? {
    id: src.oddStateId,
    oddStateId: src.oddStateId,
    priceDecimal: src.priceDecimal,
    name: src.label,
    label: src.label,
    bettable: src.bettable ?? false,
    ...sportsnewSelectionIdentity(src),
  } : null;

  // Row 1: main result from mainOdds (always available in list response).
  // Always push this row, even when a temporary lock/suspension leaves
  // home/draw/away without a live oddStateId — the individual cells already
  // render "—"/locked for a missing/unbettable odd, so gating the WHOLE row
  // on data presence just made it disappear and reappear as it locked and
  // unlocked. The slots themselves must stay fixed.
  //
  // 'wdl' (3-way, draw shown) vs 'ml' (2-way, no draw) is decided PURELY by
  // whether the API's mainOdds actually carries a draw oddStateId — never by
  // sport lookup. Per policy the backend only ever sends a draw slot for
  // soccer/ice-hockey (3-way) and omits it for basketball/volleyball/
  // baseball/eSports (2-way), so keying off the data shape here naturally
  // matches sport policy without hardcoding it — and stays correct even if
  // that policy changes on the backend side.
  rows.push({
    type: hasMainOddsDraw(mo) ? 'wdl' : 'ml',
    centerLabel: hasMainOddsDraw(mo) ? 'Draw' : null,
    homeOdd: mkOdd(mo.home),
    drawOdd: mkOdd(mo.draw),
    awayOdd: mkOdd(mo.away),
  });

  const markets = fixture.markets ?? fixture.selections ?? [];
  const sportSlug = normalizeDomesticSport(fixture.sport?.slug ?? fixture.sport ?? fixture.sportSlug);
  const defaultMarkets = selectDomesticDefaultMarkets(markets, sportSlug);
  const handicapRow = buildHandicapRow(defaultMarkets.handicap);
  const totalRow = buildTotalRow(defaultMarkets.total);
  if (handicapRow) rows.push(handicapRow);
  if (totalRow) rows.push(totalRow);

  return rows;
};

/* ── Format match time from fixture fields — fixed Asia/Seoul, never the
   browser's local timezone ── */
const fmtMatchTime = (fixture) => formatSportsMatchTime(fixture);

const DomesticPage = ({ onToggleMobileCart = () => {} }) => {
  const { selections, toggleSelection, isSelected } = useContext(SportsBetCartContext);
  const {
    categories,
    fixtures,
    loading,
    listStatus,
    listReady,
    error: listError,
    fetchFixtures,
    setMode,
	    setDisplayScope,
	    selectedSport,
	    setSelectedSport,
	    oddsFlash,
	    leagueCountryMap,
	    detailFixture,
	    detailMarkets,
	    detailLoading,
	    loadFixtureDetail,
	    clearFixtureDetail,
      page,
      totalPages,
      goToPage,
	  } = useContext(SportsnewContext);

	  const [sliderIndex, setSliderIndex] = useState(0);
	  const [searchQuery] = useState('');
	  const sportTabs = useMemo(() => domesticSportTabsFromCategories(categories), [categories]);

	  const [expandedFixtureId, setExpandedFixtureId] = useState(null);

  // Set domestic mode and default sport (soccer) on mount
  useEffect(() => {
    setMode('prematch');
    setDisplayScope('domestic');
    setSelectedSport((prev) => prev ?? { slug: sportTabs[0]?.id ?? SPORT_TABS[0].id });
  }, [setMode, setDisplayScope, setSelectedSport, sportTabs]);

  // Banner autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setSliderIndex((prev) => (prev + 1) % SLIDER_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Sync activeSport tab → SportsnewContext selectedSport
  const activeSportSlug = selectedSport
    ? (typeof selectedSport === 'object' ? selectedSport.slug : selectedSport)
    : sportTabs[0]?.id ?? SPORT_TABS[0].id;

  useEffect(() => {
    if (sportTabs.some((tab) => tab.id === activeSportSlug)) return;
    setSelectedSport(sportTabs[0] ? { slug: sportTabs[0].id } : null);
  }, [activeSportSlug, sportTabs, setSelectedSport]);

  const handleSportTab = (slug) => {
    const cat = categories.find((c) => c.slug === slug) || { slug };
    setSelectedSport(cat);
  };

  const filteredFixtures = useMemo(() => {
    const sportSlug = activeSportSlug;
    // Dedup via Map first
    const dedupMap = new Map();
    for (const f of fixtures) {
      const raw = f.fixtureId ?? f.id;
      if (raw == null) continue;
      const key = String(raw);
      if (!dedupMap.has(key)) dedupMap.set(key, f);
    }
    return Array.from(dedupMap.values()).filter((f) => {
      const fSport = typeof f.sport === 'string' ? f.sport : (f.sport?.slug ?? f.sport?.name ?? '');
      const matchesSport = !sportSlug || fSport.toLowerCase() === sportSlug.toLowerCase();
      if (!matchesSport) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const home = (f.home ?? f.home_team?.name ?? f.home_team ?? '').toLowerCase();
      const away = (f.away ?? f.away_team?.name ?? f.away_team ?? '').toLowerCase();
      const league = (f.leagueName ?? f.league?.name ?? f.league_name ?? '').toLowerCase();
      return home.includes(q) || away.includes(q) || league.includes(q);
    });
  }, [fixtures, activeSportSlug, searchQuery]);

  const listFailed = !listReady && listStatus === SPORTSNEW_LIST_STATUS.ERROR;
  const listPending = !listReady && !listFailed;
  const listEmpty = listReady && filteredFixtures.length === 0;

  useEffect(() => {
    if (!expandedFixtureId) return;
    const stillVisible = filteredFixtures.some((fixture) => (
      String(fixture.fixtureId ?? fixture.id ?? '') === expandedFixtureId
    ));
    if (stillVisible) return;
    setExpandedFixtureId(null);
    clearFixtureDetail?.();
  }, [expandedFixtureId, filteredFixtures, clearFixtureDetail]);

  const MARKET_NAME_MAP = { wdl: '승무패', ml: '승패', handicap: '핸디캡', ou: '오버/언더' };

  const pageNumbers = useMemo(() => {
    const safeTotal = Math.max(1, Number(totalPages) || 1);
    const safeCurrent = Math.max(1, Math.min(Number(page) || 1, safeTotal));
    const maxVisible = 5;
    const start = Math.max(1, Math.min(safeCurrent - 2, Math.max(safeTotal - maxVisible + 1, 1)));
    return Array.from(
      { length: Math.min(safeTotal, maxVisible) },
      (_, index) => start + index,
    ).filter((pageNumber) => pageNumber <= safeTotal);
  }, [page, totalPages]);

	  const handleFixtureExpandToggle = useCallback((fixtureId, event) => {
	    event?.stopPropagation?.();
	    if (!fixtureId) return;
	    setExpandedFixtureId((prev) => {
	      if (prev === fixtureId) {
	        clearFixtureDetail?.();
	        return null;
	      }
	      loadFixtureDetail?.(fixtureId);
	      return fixtureId;
	    });
	  }, [clearFixtureDetail, loadFixtureDetail]);

  const handleOddsClick = (fixture, odd, rowType = 'wdl', marketNameOverride = null) => {
    if (!odd || !odd.bettable || oddIdOf(odd) == null) return;
    const homeTeam = fixture.home ?? fixture.home_team?.name ?? fixture.home_team ?? '';
    const awayTeam = fixture.away ?? fixture.away_team?.name ?? fixture.away_team ?? '';
    const fixtureId = fixture.fixtureId ?? fixture.id;
    const marketName = marketNameOverride ?? MARKET_NAME_MAP[rowType] ?? rowType;
    toggleSelection({
      odds_id: odd.id,
      match_id: fixtureId,
      market_id: odd.market_id ?? null,
      market_group_key: `${fixtureId}:${odd.market_id ?? rowType}`,
      providerOddId: odd.providerOddId ?? odd.provider_odd_id ?? null,
      oddIdentityKey: odd.oddIdentityKey ?? odd.odd_identity_key ?? null,
      ...sportsnewSelectionIdentity(odd),
      odds: odd.priceDecimal ?? odd.odds ?? odd.price,
      match_name: `${homeTeam} vs ${awayTeam}`,
      market_name: marketName,
      selection_name_display: getSelectionLabel(odd, { match: fixture, fallback: odd.label ?? odd.name ?? odd.selectionName }),
    });
  };

  return (
    <div className="dp_page">
      <div className="dp_shell">

        {/* ═══ LEFT PANEL: Match List ═══ */}
        <div className="dp_left_col">

          {/* Hero Slider */}
          <div className="dp_hero">
            <div
              className="dp_hero_track"
              style={{ transform: `translateX(-${sliderIndex * 100}%)` }}
            >
              {SLIDER_IMAGES.map(({ desktop, mobile }, idx) => (
                <picture key={desktop} className="dp_hero_picture">
                  <source media="(max-width: 575px)" srcSet={mobile} />
                  <img src={desktop} alt={`slide-${idx}`} className="dp_hero_img" />
                </picture>
              ))}
            </div>
          </div>

          {/* Sport Navigation Tabs */}
          <div className="dp_sport_tabs">
            {sportTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`dp_sport_tab ${activeSportSlug === tab.id ? 'dp_sport_tab_active' : ''}`}
                onClick={() => handleSportTab(tab.id)}
              >
                <img src={tab.icon} alt={tab.label} className="dp_sport_icon_img" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Match List */}
          <div className="dp_match_list">
            {listPending && <DpMatchListSkeleton count={4} />}

            {listFailed && (
              <div className="dp_no_data" role="alert">
                <span>{listError || '경기 목록을 불러오지 못했습니다.'}</span>
                <button type="button" onClick={() => fetchFixtures()} disabled={loading}>다시 시도</button>
              </div>
            )}

            {listEmpty && (
              <div className="dp_no_data">진행 중인 경기가 없습니다.</div>
            )}

            {listReady && filteredFixtures.map((fixture) => {
              const homeTeam = fixture.home ?? fixture.home_team?.name ?? fixture.home_team ?? '홈팀';
              const awayTeam = fixture.away ?? fixture.away_team?.name ?? fixture.away_team ?? '원정팀';
              const homeLogo = resolveTeamLogo(fixture.homeLogo ?? fixture.home_team?.logo ?? fixture.home_logo, 'home');
              const awayLogo = resolveTeamLogo(fixture.awayLogo ?? fixture.away_team?.logo ?? fixture.away_logo, 'away');
              const leagueName = fixture.leagueName ?? fixture.league?.name ?? fixture.league_name ?? '';
              // No nested league/country object exists on a real fixture row
              // (confirmed against real captured list payloads) — country
              // name comes from the navigation-tree cross-reference instead
              // (see leagueCountryMap in SportsnewContext.jsx), and the flag
              // is derived from that name client-side, not a backend image.
              const countryName = resolveSportsnewCountryName(fixture, leagueCountryMap);
              const countryFlag = countryFlagEmoji(countryName);
              const leagueDisplayName = countryName ? `${countryName} - ${leagueName}` : leagueName;
	              const timeLabel = fmtMatchTime(fixture);
	              const fixtureId = String(fixture.fixtureId ?? fixture.id ?? '');
	              const previewMarkets = Array.isArray(fixture.marketsPreview) ? fixture.marketsPreview : [];
	              const currentDetailFixtureId = String(detailFixture?.fixtureId ?? detailFixture?.id ?? '');
	              const expanded = expandedFixtureId === fixtureId;
	              const fetchedMarkets = expanded && currentDetailFixtureId === fixtureId && Array.isArray(detailMarkets)
	                ? detailMarkets
	                : null;
	              const isMarketsLoading = expanded && detailLoading && !fetchedMarkets?.length;
	              const fixtureSportSlug = normalizeDomesticSport(
	                typeof fixture.sport === 'string' ? fixture.sport : (fixture.sport?.slug ?? activeSportSlug)
	              );
	              const extraMarkets = expanded ? selectDomesticAdditionalMarkets(fetchedMarkets ?? [], fixtureSportSlug) : [];
	              const defaultRowMarkets = previewMarkets.length ? previewMarkets : (fetchedMarkets ?? []);
	              const oddsRows = extractCrossRows(
	                { ...fixture, markets: defaultRowMarkets }
	              );

              return (
	                <div
	                  key={String(fixture.fixtureId ?? fixture.id)}
	                  className="dp_match_card"
	                >
                  {/* Card Header */}
                  <div className="dp_card_header">
                    <div className="dp_header_left">
                      <span className="dp_card_flag_emoji" role="img" aria-label={countryName || '국제'}>{countryFlag}</span>
                      <span className="dp_card_league">{leagueDisplayName}</span>
                      {leagueDisplayName && <span className="dp_card_sep">|</span>}
                      <span className="dp_card_sport">{getSportsnewSportLabel(fixture)}</span>
                    </div>
                    <div className="dp_header_right">
                      <span className="dp_card_time">{timeLabel}</span>
                      <button
                        type="button"
                        className={`dp_expand_btn ${expanded ? 'dp_expand_btn_open' : ''}`}
                        onClick={(event) => handleFixtureExpandToggle(fixtureId, event)}
                        aria-label={expanded ? '마켓 접기' : '전체 마켓 펼치기'}
                      >
                        {expanded ? <Minus size={14} /> : <Plus size={14} />}
                      </button>
                      <div className="dp_header_stats_icon"><BarChart2 size={16} /></div>
                    </div>
                  </div>

                  {/* Odds Rows */}
                  <div className="dp_card_body_table">
                    {oddsRows.length === 0 && (
                      <div className="dp_no_odds">배당 정보 없음</div>
                    )}

                    {oddsRows.map((row, rowIdx) => {
                      const homeOdd = row.homeOdd;
                      const midOdd = row.drawOdd;
                      const awayOdd = row.awayOdd;
                      const homeKey = homeOdd?.id;
                      const midKey = midOdd?.id;
                      const awayKey = awayOdd?.id;
                      const homeUnavailable = !homeOdd || !homeOdd.bettable || oddIdOf(homeOdd) == null;
                      const midUnavailable = !midOdd || !midOdd.bettable || oddIdOf(midOdd) == null;
                      const awayUnavailable = !awayOdd || !awayOdd.bettable || oddIdOf(awayOdd) == null;

                      // Handicap/Over-Under: line folded into each side's own
                      // label instead of a separate middle "line" column —
                      // "Team (-0.5)" / "Team (+0.5)" and "Over 9.5" / "Under 9.5".
                      const homeLabel = row.type === 'handicap'
                        ? (row.homeLine ? `${homeTeam} (${row.homeLine})` : homeTeam)
                        : row.type === 'ou' ? `오버${row.line ? ` ${row.line}` : ''}`
                        : homeTeam;
                      const awayLabel = row.type === 'handicap'
                        ? (row.awayLine ? `${awayTeam} (${row.awayLine})` : awayTeam)
                        : row.type === 'ou' ? `언더${row.line ? ` ${row.line}` : ''}`
                        : awayTeam;
                      const isHandicap = row.type === 'handicap';

                      return (
                        <div key={rowIdx} className="dp_card_odds_row">
                          {/* Home */}
                          <button
                            type="button"
                            className={`dp_card_odds_cell dp_cell_left ${isSelected(homeKey) ? 'dp_cell_selected' : ''} ${homeUnavailable ? 'dp_cell_suspended dp_cell_locked' : ''} ${oddsFlash[homeKey] ? `dp_odds_flash_${oddsFlash[homeKey]}` : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOddsClick(fixture, homeOdd, row.type); }}
                            disabled={homeUnavailable}
                          >
                            <div className="dp_cell_team_wrap">
                              <img src={homeLogo} className="dp_odds_jersey_img" alt="Home" />
                              <span className={`dp_cell_team_name ${isHandicap ? 'dp_cell_team_name_handicap' : ''}`}>
                                {isHandicap ? (
                                  <>
                                    <span className="dp_cell_handicap_team">{homeTeam}</span>
                                    {row.homeLine && <span className="dp_cell_handicap_line">({row.homeLine})</span>}
                                  </>
                                ) : homeLabel}
                              </span>
                            </div>
                            <span className="dp_cell_odds_value">
                              {homeUnavailable && <Lock size={10} strokeWidth={2.5} className="dp_lock_icon" />}
                              {homeUnavailable ? '—' : fmtOdds(homeOdd.priceDecimal)}
                            </span>
                          </button>

	                          {/* Mid slot: 2-way result rows have no center slot; handicap/OU use a fixed marker. */}
	                          {row.type === 'wdl' ? (
	                            <button
	                              type="button"
                              className={`dp_card_odds_cell dp_cell_mid ${isSelected(midKey) ? 'dp_cell_selected' : ''} ${midUnavailable ? 'dp_cell_suspended dp_cell_locked' : ''} ${oddsFlash[midKey] ? `dp_odds_flash_${oddsFlash[midKey]}` : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleOddsClick(fixture, midOdd, row.type); }}
                              disabled={midUnavailable}
	                            >
	                              <span className="dp_cell_odds_badge draww">Draw</span>
                              <span className="dp_cell_odds_value">
                                {midUnavailable && <Lock size={10} strokeWidth={2.5} className="dp_lock_icon" />}
                                {midUnavailable ? '—' : fmtOdds(midOdd.priceDecimal)}
	                              </span>
	                            </button>
	                          ) : row.centerLabel ? (
	                            <div className="dp_card_odds_cell dp_cell_mid dp_cell_mid_label">
	                              <span className="dp_cell_odds_badge dp_cell_marker_badge">{row.centerLabel}</span>
	                            </div>
	                          ) : null}

                          {/* Away */}
                          <button
                            type="button"
                            className={`dp_card_odds_cell dp_cell_right ${isSelected(awayKey) ? 'dp_cell_selected' : ''} ${awayUnavailable ? 'dp_cell_suspended dp_cell_locked' : ''} ${oddsFlash[awayKey] ? `dp_odds_flash_${oddsFlash[awayKey]}` : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOddsClick(fixture, awayOdd, row.type); }}
                            disabled={awayUnavailable}
                          >
                            <span className="dp_cell_odds_value">
                              {awayUnavailable && <Lock size={10} strokeWidth={2.5} className="dp_lock_icon" />}
                              {awayUnavailable ? '—' : fmtOdds(awayOdd.priceDecimal)}
                            </span>
                            <div className="dp_cell_team_wrap">
                              <span className={`dp_cell_team_name ${isHandicap ? 'dp_cell_team_name_handicap' : ''}`}>
                                {isHandicap ? (
                                  <>
                                    <span className="dp_cell_handicap_team">{awayTeam}</span>
                                    {row.awayLine && <span className="dp_cell_handicap_line">({row.awayLine})</span>}
                                  </>
                                ) : awayLabel}
                              </span>
                              <img src={awayLogo} className="dp_odds_jersey_img" alt="Away" />
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {expanded && (
                    <div className="dp_expanded_markets" onClick={(event) => event.stopPropagation()}>
                      {isMarketsLoading && (
                        <div className="dp_expanded_loading">
                          <Loader size={14} className="dp_spin" />
                          <span>마켓 불러오는 중</span>
                        </div>
                      )}

	                      {!isMarketsLoading && extraMarkets.length === 0 && (
	                        <div className="dp_expanded_empty">추가 마켓이 없습니다.</div>
	                      )}

	                      {!isMarketsLoading && extraMarkets.map((market) => {
	                        const marketName = marketDisplayName(market);
	                        const marketKey = market.marketGroupId ?? market.marketStateId ?? market.marketId ?? market.id ?? marketName;
                        const marketOdds = marketOddsFromMarket(market);
	                        if (marketOdds.length === 0) return null;
	                        const gridCols = Math.min(3, Math.max(2, marketOdds.length));
	                        const role = domesticMarketRole(market);

	                        return (
	                          <div key={marketKey} className="dp_expanded_market">
	                            <div className="dp_expanded_market_title">
	                              <span>{marketName}</span>
	                              {role && <span className="dp_expanded_market_role">{role === 'handicap' ? 'HANDI' : role === 'total' ? 'O/U' : '승패'}</span>}
	                            </div>
                            <div className={`dp_expanded_odds_grid dp_expanded_cols_${gridCols}`}>
                              {marketOdds.map((odd, index) => {
                                const oddId = odd.oddStateId ?? odd.id;
                                const priceDecimal = odd.priceDecimal ?? odd.price_decimal ?? odd.odds ?? odd.price;
                                const bettable = odd.bettable ?? odd.isBettable ?? odd.is_bettable ?? false;
                                const label = getSelectionLabel(odd, {
                                  match: fixture,
                                  fallback: odd.displayName ?? odd.name ?? odd.selectionName ?? odd.selection,
                                });
                                const cartOdd = {
                                  ...odd,
                                  id: oddId,
                                  priceDecimal,
                                  bettable,
                                  label,
                                  market_id: marketKey,
                                };
                                const selected = isSelected(oddId);
                                return (
                                  <button
                                    key={oddId ?? `${marketKey}:${index}`}
                                    type="button"
                                    className={`dp_expanded_odd_cell ${selected ? 'dp_cell_selected' : ''} ${bettable ? '' : 'dp_cell_suspended'} ${oddsFlash[oddId] ? `dp_odds_flash_${oddsFlash[oddId]}` : ''}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOddsClick(fixture, cartOdd, String(marketKey), marketName);
                                    }}
                                    disabled={!bettable}
                                  >
                                    <span className="dp_expanded_odd_label">{label}</span>
                                    <span className="dp_expanded_odd_value">
                                      {!bettable && <Lock size={10} strokeWidth={2.5} className="dp_lock_icon" />}
                                      {fmtOdds(priceDecimal)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!loading && totalPages > 1 && (
            <nav className="dp_pagination" aria-label="국내형 경기 페이지">
              <button
                type="button"
                className="dp_page_btn dp_page_edge_btn"
                disabled={page <= 1}
                onClick={() => goToPage(1)}
              >
                처음
              </button>
              <button
                type="button"
                className="dp_page_btn dp_page_arrow_btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                aria-label="이전 페이지"
              >
                ‹
              </button>
              <div className="dp_page_numbers" aria-label={`${page} / ${totalPages}`}>
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`dp_page_btn dp_page_number_btn ${page === pageNumber ? 'dp_page_btn_active' : ''}`}
                    onClick={() => goToPage(pageNumber)}
                    aria-current={page === pageNumber ? 'page' : undefined}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="dp_page_btn dp_page_arrow_btn"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                aria-label="다음 페이지"
              >
                ›
              </button>
              <button
                type="button"
                className="dp_page_btn dp_page_edge_btn"
                disabled={page >= totalPages}
                onClick={() => goToPage(totalPages)}
              >
                끝
              </button>
            </nav>
          )}
        </div>

	      </div>

      {/* Floating betslip button for mobile */}
      <button
        type="button"
        className="ipp_mobile_cart_fab"
        onClick={onToggleMobileCart}
      >
        <span className="ipp_mobile_cart_icon_wrap">
          <ReceiptText size={18} />
          {selections.length > 0 && (
            <span className="ipp_mobile_cart_badge">{selections.length}</span>
          )}
        </span>
        <span>베팅카트</span>
      </button>

    </div>
  );
};

export default DomesticPage;
